import { SecurityLevel } from "expo-local-authentication";
import { useEffect, useState } from "react";
import { Badge } from "~/components/feedback/Badge";
import { PasscodeInput } from "~/components/form/auth/PasscodeInput";
import { FieldGroup } from "~/components/form/FieldGroup";
import { SwitchField } from "~/components/form/SwitchField";
import { ListingItem } from "~/components/layout/ListingItem";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Nothing } from "~/components/permission/Nothing";
import { Only } from "~/components/permission/Only";
import { Padding } from "~/constants/layout";
import { Font } from "~/constants/typography";
import { useFeedback } from "~/providers/FeedbackProvider";
import { attemptBiometrics, type PasscodeString, useSession } from "~/providers/SessionProvider";
import { useTheme } from "~/providers/ThemeProvider";

const MAX_ATTEMPTS = 3;

enum Action {
    idle,
    setPasscode,
    changePasscode,
    removePasscode,
    setBiometrics,
    removeBiometrics,
    forceRemoveBiometrics,
}

export default function AppSecurity() {
    const { text } = useTheme();
    const { addToast } = useFeedback();
    const { enrollable, level, enrolled, setEnrollment, setPasscode, clearPasscode, checkPasscode, checkEnrollment } =
        useSession();

    const [error, setError] = useState<string | null>(null);
    const [action, setAction] = useState<Action>(Action.idle);
    const [attempts, setAttempts] = useState(MAX_ATTEMPTS);
    const [code, setCode] = useState<PasscodeString | null>(null);

    const onCancel = () => {
        setAction(Action.idle);
        setError(null);
        setCode(null);
        setAttempts(MAX_ATTEMPTS);
        checkEnrollment();
    };

    const onFail = (error?: string) => {
        setAttempts(attempts - 1);
        if (attempts <= 1) {
            onCancel();
        }
        setError(error ?? "Could not confirm.");
    };

    useEffect(() => {
        action === Action.idle || setAttempts(MAX_ATTEMPTS);
    }, [action]);

    const updatePasscode = (code: PasscodeString) => {
        onCancel();
        setPasscode(code);
        addToast({ content: "Passcode updated." });
    };

    const removePasscode = () => {
        onCancel();
        clearPasscode();
        addToast({ content: "Passcode removed." });
    };

    const enableBiometrics = () => {
        onCancel();
        setEnrollment(level);
        addToast({ content: "Biometrics enabled." });
    };

    const removeBiometrics = () => {
        onCancel();
        setEnrollment(Math.min(enrolled, SecurityLevel.SECRET));
        addToast({ content: "Biometrics disabled." });
    };

    const togglePasscode = (enabled: boolean) => {
        onCancel();

        if (enabled) {
            setAction(Action.setPasscode);
        } else {
            setAction(Action.removePasscode);
        }
    };

    const toggleBiometrics = async (enabled: boolean) => {
        onCancel();

        if (enabled) {
            setAction(Action.setBiometrics);
        } else {
            setAction(Action.removeBiometrics);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(removeBiometrics): action
    // biome-ignore lint/correctness/useExhaustiveDependencies(enableBiometrics): action
    // biome-ignore lint/correctness/useExhaustiveDependencies(onCancel): action
    // biome-ignore lint/correctness/useExhaustiveDependencies(onFail): action
    useEffect(() => {
        if (action === Action.setBiometrics) {
            attemptBiometrics({
                promptMessage: "Confirm to enable.",
                onSuccess: enableBiometrics,
                onCancel,
                onError: (error?: string) => {
                    onFail(error ?? "Could not enable biometrics");
                },
            });
        } else if (action === Action.removeBiometrics) {
            attemptBiometrics({
                promptMessage: "Confirm to disable biometrics.",
                disableDeviceFallback: false,
                onSuccess: removeBiometrics,
                onCancel,
                onResult(result) {
                    if (result.error === "lockout" || attempts <= 1) {
                        setAction(Action.forceRemoveBiometrics);
                        setError(result.warning ?? "You have been locked out.");
                    } else {
                        onFail(result.warning ?? "Unknown Biometrics Error");
                    }
                },
            });
        }
    }, [action, attempts]);

    const handle_confirm = (confirm: PasscodeString) => {
        if (action === Action.setPasscode && code === confirm) {
            updatePasscode(code);
        } else if (action === Action.setPasscode && code === null) {
            setCode(confirm);
        } else if (action === Action.changePasscode && checkPasscode(confirm)) {
            setAction(Action.setPasscode);
        } else if (action === Action.removePasscode && checkPasscode(confirm)) {
            removePasscode();
        } else if (action === Action.forceRemoveBiometrics && checkPasscode(confirm)) {
            removeBiometrics();
        } else {
            onFail("Wrong passcode.");
        }
    };

    const handling_passcode = [
        Action.setPasscode,
        Action.changePasscode,
        Action.forceRemoveBiometrics,
        Action.removePasscode,
    ].includes(action);

    const title =
        (action === Action.setPasscode && code === null && "Add your new passcode") ||
        (action === Action.setPasscode && code !== null && "Confirm your new passcode") ||
        "Enter your current passcode";

    return (
        <Page spacer onLayout={onCancel}>
            <SectionHeader title="App security" icon="lock">
                Protect your app with a passcode and Face ID or Touch ID.
            </SectionHeader>
            <Only when={error && action === Action.idle}>
                <Badge
                    icon="warning-outline"
                    size="lg"
                    variant="danger"
                    title={error as string}
                    textStyle={[Font.regular, Font.md]}
                />
            </Only>
            <FieldGroup>
                <SwitchField
                    label="Passcode"
                    value={enrolled >= SecurityLevel.SECRET || handling_passcode}
                    disabled={handling_passcode}
                    onValueChange={togglePasscode}
                />
                <Only when={enrolled >= SecurityLevel.SECRET}>
                    <ListingItem title="Change passcode" onPress={() => setAction(Action.changePasscode)} last />
                </Only>
            </FieldGroup>
            <Only when={enrolled === SecurityLevel.NONE}>
                <Badge
                    icon="info-circle"
                    size="lg"
                    title="After you add a passcode, you can set up Face ID or Touch ID."
                    textStyle={[Font.regular, Font.md, text.primary]}
                />
            </Only>
            <Only when={enrolled >= SecurityLevel.SECRET}>
                <FieldGroup>
                    <SwitchField
                        label="Biometrics"
                        disabled={!enrollable}
                        value={enrolled >= SecurityLevel.BIOMETRIC_WEAK}
                        onValueChange={toggleBiometrics}
                        hint={enrollable ? null : "Biometrics are not available"}
                    />
                </FieldGroup>
            </Only>
            {handling_passcode ? (
                <FullPageModal cancelable onRequestClose={onCancel}>
                    <PasscodeInput
                        onSubmit={handle_confirm}
                        title={title}
                        style={Padding.top_xxl}
                        error={error}
                        attempts={attempts < MAX_ATTEMPTS ? attempts : null}
                        onChange={(code) => {
                            if (code.length < 4) {
                                error && setError(null);
                            }
                        }}
                    />
                </FullPageModal>
            ) : (
                <Nothing />
            )}
        </Page>
    );
}
